<template>
  <div class="d-flex flex-row justify-content-between">
    <div class="min-h-screen flex overflow-x-scroll py-12 kanban flex-column">
      <div class="flex flex-row">
        <div
          v-for="(column, index) in draggableColumns"
          :key="column.name"
          class="bg-gray-100 rounded-lg py-3 column-width rounded mr-2 column"
        >
          <div
            :class="[
              'd-flex flex-row align-items-center column-header',
              colors[index],
            ]"
          >
            <p class="column-title mb-0">
              {{ column.name }}
            </p>
            <p class="column-content-count mb-0">
              {{ column.tasks.length }}
            </p>
          </div>

          <draggable
            :list="column.tasks"
            :animation="200"
            ghost-class="ghost-card"
            group="tasks"
            class="draggable-task"
            @change="
              $emit('handle-drag-and-drop', {
                $event,
                companyTaskStatusId: column.id,
              })
            "
          >
            <task-card
              v-for="task in column.tasks"
              :key="task.id"
              :task="task"
              :selected="selectedTaskIds.includes(task.id)"
              :knowledge="mappedKnowledges[task.knowledgeId]"
              :animatedTaskId="animatedTaskId"
              class="mt-3 cursor-move ghost-task"
              @open-focus-task="$emit('open-focus-task', $event)"
              @handle-show-bulk-actions="$emit('handle-show-bulk-actions')"
              @update-selected-tasks="$emit('update-selected-tasks', $event)"
              @add-upvote="$emit('add-upvote', $event)"
              @remove-upvote="$emit('remove-upvote', $event)"
            ></task-card>
          </draggable>
        </div>
      </div>

      <div v-if="isFilteredAndEmpty" class="empty-filtered">
        <div class="empty-msg">
          {{ $t('task-view.empty-state.empty-msg') }}
        </div>
        <img src="@/assets/empty-task.svg" alt="empty-task" class="empty-img" />
        <div class="empty-explanation">
          {{ $t('task-view.empty-state.empty-explaination') }}
        </div>
        <el-button type="primary" @click="$emit('empty-filters')"
          >{{ $t('task-view.empty-state.empty-button') }}
        </el-button>
      </div>
    </div>
    <task-focus-modal
      v-if="modals.focus"
      :loader="loadingFocusTask"
      :display="modals.focus"
      :task="focusTask"
      @close="$emit('close-focus-modal')"
      @handle-update-assignee="$emit('handle-update-assignee', $event)"
      @handle-update-status="$emit('handle-update-status', $event)"
      @handle-is-archive="$emit('handle-is-archive', $event)"
      @add-upvote="$emit('add-upvote', $event)"
      @remove-upvote="$emit('remove-upvote', $event)"
      @handle-new-comment="$emit('handle-new-comment', $event)"
      @delete-comment="$emit('delete-comment', $event)"
      @update-rating="$emit('update-rating', $event)"
      @delete-rating="$emit('delete-rating', $event)"
    ></task-focus-modal>
    <task-assignee-modal
      v-if="modals.assignee"
      :display="modals.assignee"
      @close="$emit('close-assignee-modal')"
      @handle-update-assignee="$emit('handle-update-assignee', $event)"
    ></task-assignee-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import TaskCard from '../commons/TaskCard.vue';
import TaskAssigneeModal from '../commons/TaskAssigneeModal.vue';
import TaskFocusModal from '../commons/TaskFocusModal.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'task-board',
  components: {
    TaskCard,
    draggable,
    TaskAssigneeModal,
    TaskFocusModal,
  },
  props: {
    columns: Array,
    modals: {
      type: Object,
      default: () => {},
    },
    focusTask: {
      type: Object,
      default: () => {},
    },
    showBulkActions: {
      type: Boolean,
      default: false,
    },
    selectedTaskIds: {
      type: Array,
      default: () => [],
    },
    animatedTaskId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isLoading: false,
      companyTaskStatus: null,
      draggableColumns: null,
      colors: ['grey', 'orange', 'blue', 'green', 'purple'],
    };
  },

  mounted() {
    this.draggableColumns = [...this.columns];
  },

  watch: {
    columns() {
      this.draggableColumns = [...this.columns];
    },
  },
  computed: {
    ...mapGetters('taskModule', ['loadingFocusTask', 'isFilteredAndEmpty']),
    ...mapGetters('knowledgeModule', ['mappedKnowledges']),
  },
};
</script>

<style lang="scss" scoped>
.column-header {
  padding-bottom: 9px;
}
.column-title {
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.02em;
  text-align: left;
  margin-right: 10px;
}
.column-content-count {
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.02em;
  text-align: center;
  background-color: $grey-2-mayday;
  color: $grey-6-mayday;
  width: 20px;
  height: 15px;
  border-radius: 4px;
}
.grey {
  border-bottom: 4px solid $grey-4-mayday;
}
.orange {
  border-bottom: 4px solid $yellow-mayday;
}
.blue {
  border-bottom: 4px solid $blue-mayday;
}
.green {
  border-bottom: 4px solid $green-mayday;
}
.purple {
  border-bottom: 4px solid $red-1-mayday;
}
.column-width {
  width: 24.7%;
  padding-left: 8px;
  padding-right: 8px;
}

.column-width:first-child,
.column-width:last-child {
  width: 24.9%;
}

.column-width:first-child {
  padding-left: 15px;
}
.column-width:last-child {
  padding-right: 15px;
}

.kanban {
  width: 100%;
}
.ghost-card {
  background: $grey-1-mayday;
  height: 2px;
  border-top: 2px solid $blue-mayday;
  :deep() .task-box {
    visibility: hidden;
  }
}

.draggable-task {
  height: calc(100vh - 80px);
  overflow-y: scroll;
}

.empty-filtered {
  position: absolute;
  top: calc(50% - 150px);
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.empty-msg {
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.02em;
  margin-bottom: 16px;
}

.empty-explanation {
  font-size: 13px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: $grey-7-mayday;
  margin-bottom: 16px;
  max-width: 313px;
}
</style>
